import { sidebarConfig, useSidebarOpenState } from '@backstage/core-components';
import { iconsApiRef, useApi } from '@backstage/frontend-plugin-api';
import { useUserProfile } from '@backstage/plugin-user-settings';
import {
  ButtonIcon,
  Flex,
  Link,
  Menu,
  MenuItem,
  MenuSection,
  MenuSeparator,
  MenuTrigger,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import { makeStyles } from '@material-ui/core';
import type { Theme } from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';
import { RiShutDownLine } from '@remixicon/react';
import {
  IconBox,
  sidebarPillInset,
  RailButton,
  userDisplayName,
  useSignOut,
} from '@internal/plugin-waadoo-core';
import {
  SidebarThemeSwitch,
  themeModeIcons,
  useThemeMode,
} from './SidebarThemeSwitch';
import { THEME_MODES, isThemeMode, themeModeLabels } from './themeMode';
import type { ThemeMode } from './themeMode';

/**
 * The height core-components gives every SidebarItem row (`root` in
 * `makeSidebarStyles`, Items.esm.js: a literal 48, not in `sidebarConfig`):
 * the user row takes it too, so it reads as one of the entries below.
 */
const NAV_ITEM_HEIGHT = 48;

const SIGN_OUT_LABEL = 'Se déconnecter';

// The rules below are not in a cascade layer, so they win over BUI's own,
// which all sit in `@layer components`.
//
// The block sits on the menu's grid, as core-components lays a SidebarItem
// out (Items.esm.js): a row `drawerWidthOpen` wide, a centred icon in a
// column `iconContainerWidth` wide whose right margin is `-theme.spacing(2)`,
// so the label starts 16px before the column ends. The power button and the
// theme switch end, and the switch starts, on the edges of the active item's
// pill (`sidebarPillInset`, themes.ts).
const useStyles = makeStyles<Theme>(theme => {
  // The size SidebarItem gives every nav icon (core-components' `itemIcon`
  // class: 1.5em of `theme.typography.fontSize`), so the person icon matches
  // the entries below it.
  const navIconSize = theme.typography.fontSize * 1.5;

  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--bui-space-1)',
      paddingBottom: 'var(--bui-space-2)',
      // The drawer lays its children out with `align-items: flex-start`
      // (core-components' Bar.esm.js), which would shrink the block to its
      // content: the explicit width of a SidebarItem row puts the grid above
      // in the same coordinates as the menu's.
      width: sidebarConfig.drawerWidthOpen,
    },
    // Closed — the compact menu, 72 px wide: the person icon, then the
    // theme button, each centred in a row like the nav icons in their 72 px
    // column.
    collapsed: {
      width: sidebarConfig.drawerWidthClosed,
      gap: 0,
      paddingBottom: 0,
    },
    collapsedRow: {
      display: 'flex',
      width: sidebarConfig.drawerWidthClosed,
      height: NAV_ITEM_HEIGHT,
      alignItems: 'center',
      justifyContent: 'center',
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      height: NAV_ITEM_HEIGHT,
      paddingRight: sidebarPillInset.inline,
    },
    profile: {
      display: 'flex',
      alignItems: 'center',
      alignSelf: 'stretch',
      flex: '1 1 auto',
      // Lets the name shrink below its own width, so the ellipsis can apply.
      minWidth: 0,
      textDecoration: 'none',
      '&:hover': { textDecoration: 'none' },
    },
    // The nav icons' column and colour, not the link's: the icon reads as
    // one of them, and the name starts where their labels do.
    icon: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      width: sidebarConfig.iconContainerWidth,
      marginRight: -theme.spacing(2),
      lineHeight: 0,
      color: theme.palette.navigation.color,
      '& svg': {
        width: navIconSize,
        height: navIconSize,
        fontSize: 'inherit',
      },
    },
    // The name ends a space before the power button.
    name: {
      minWidth: 0,
      paddingRight: 'var(--bui-space-2)',
    },
    // The closed drawer's way in: the person icon, as a button.
    menuTrigger: {
      color: theme.palette.navigation.color,
      '& svg': {
        width: navIconSize,
        height: navIconSize,
      },
    },
    // A bare glyph in the secondary ink at rest; danger ink, border and
    // background when hovered or focused, a destructive action being
    // announced before it is taken. The border is there, transparent, at
    // rest, so that it does not move the glyph when it appears.
    signOut: {
      width: 28,
      height: 28,
      flexShrink: 0,
      boxSizing: 'border-box',
      border: '1px solid transparent',
      borderRadius: 7,
      background: 'transparent',
      color: 'var(--bui-fg-secondary)',
      '& svg': { width: 17, height: 17 },
      '&[data-hovered], &[data-focus-visible]': {
        color: 'var(--bui-fg-danger)',
        borderColor: 'var(--bui-border-danger)',
        background: 'var(--bui-bg-danger)',
      },
    },
    // The switch spans the active item's pill, edge to edge.
    themeSwitch: {
      display: 'flex',
      paddingLeft: sidebarPillInset.inline,
      paddingRight: sidebarPillInset.inline,
    },
  };
});

/**
 * The signed-in user under the logo.
 *
 * Open drawer: a person icon and the name, linking to the settings page, a
 * power button that signs out of Backstage and then of Authelia
 * (`useSignOut`), and under them the theme switch (SidebarThemeSwitch).
 *
 * Closed drawer — the compact menu (72 px, too narrow for a name or three
 * segments): the person icon, a button opening a menu with « Profil », the
 * three theme modes and « Se déconnecter »; under it the theme button
 * (ThemeCycleButton).
 *
 * Not rendered on a phone at all: see Sidebar.tsx.
 */
export const SidebarUserBlock = () => {
  const classes = useStyles();
  const { isOpen } = useSidebarOpenState();
  // It reports a failed sign-out itself.
  const { signOut, pending: signingOut } = useSignOut();
  // The hook the Settings avatar at the bottom of the sidebar already uses:
  // it reports a failed read itself.
  const { profile, backstageIdentity } = useUserProfile();
  const { mode, select } = useThemeMode();
  const checkIcon = useApi(iconsApiRef).icon('check');

  // Absent while the identity loads, or if reading it failed (the hook has
  // already reported that): the sign-out stays reachable regardless.
  const name = backstageIdentity
    ? userDisplayName(profile.displayName, backstageIdentity.userEntityRef)
    : undefined;
  const profileLabel = name ? `Profil — ${name}` : 'Profil';

  if (!isOpen) {
    return (
      <div className={`${classes.root} ${classes.collapsed}`}>
        <div className={classes.collapsedRow}>
          <MenuTrigger>
            <TooltipTrigger>
              <ButtonIcon
                variant="tertiary"
                size="medium"
                aria-label={profileLabel}
                icon={<PersonIcon />}
                isPending={signingOut}
                className={classes.menuTrigger}
              />
              <Tooltip placement="right">{name ?? 'Profil'}</Tooltip>
            </TooltipTrigger>
            <Menu aria-label={profileLabel} placement="right top">
              {/* The settings page, as the name links to in the open drawer. */}
              <MenuItem
                id="waadoo-profile"
                href="/settings"
                textValue={profileLabel}
                iconStart={<PersonIcon aria-hidden focusable="false" />}
              >
                {name ?? 'Profil'}
              </MenuItem>
              <MenuSeparator />
              {/* The selection is the section's, not the menu's: the theme
                modes are `menuitemradio` rows with `aria-checked`, and
                « Se déconnecter » below stays a plain `menuitem`. BUI's
                MenuSection forwards these props to react-aria's (its
                definition declares only title, children and className). */}
              <MenuSection
                title="Thème"
                selectionMode="single"
                disallowEmptySelection
                selectedKeys={[mode]}
                onSelectionChange={keys => {
                  const [selected] = keys;
                  if (isThemeMode(selected)) {
                    select(selected);
                  }
                }}
              >
                {THEME_MODES.map(option => (
                  <MenuItem
                    key={option}
                    // react-aria keys a collection item by its `id`, which
                    // `selectedKeys` above matches.
                    id={option}
                    textValue={themeModeLabels[option]}
                    iconStart={themeModeIcons[option]}
                  >
                    {/* The check is drawn here: BUI's MenuItem shows none for
                      a selected row (see BranchSwitcher). */}
                    <Flex align="center" gap="2">
                      {themeModeLabels[option]}
                      {option === mode && checkIcon && (
                        <IconBox size="small">{checkIcon}</IconBox>
                      )}
                    </Flex>
                  </MenuItem>
                ))}
              </MenuSection>
              <MenuSeparator />
              <MenuItem
                id="waadoo-sign-out"
                textValue={SIGN_OUT_LABEL}
                color="danger"
                iconStart={<RiShutDownLine aria-hidden focusable="false" />}
                onAction={() => void signOut()}
              >
                {SIGN_OUT_LABEL}
              </MenuItem>
            </Menu>
          </MenuTrigger>
        </div>
        <ThemeCycleButton mode={mode} select={select} />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.row}>
        {name ? (
          <TooltipTrigger>
            <Link
              href="/settings"
              aria-label={profileLabel}
              color="primary"
              standalone
              className={classes.profile}
            >
              <span className={classes.icon}>
                <PersonIcon />
              </span>
              <Text
                variant="body-small"
                weight="bold"
                truncate
                className={classes.name}
              >
                {name}
              </Text>
            </Link>
            <Tooltip placement="right">{name}</Tooltip>
          </TooltipTrigger>
        ) : (
          // Holds the button at the right edge while the name loads.
          <span className={classes.profile} />
        )}
        <TooltipTrigger>
          <ButtonIcon
            variant="tertiary"
            size="small"
            aria-label={SIGN_OUT_LABEL}
            icon={<RiShutDownLine />}
            isPending={signingOut}
            className={classes.signOut}
            onPress={() => void signOut()}
          />
          <Tooltip placement="right">{SIGN_OUT_LABEL}</Tooltip>
        </TooltipTrigger>
      </div>
      <div className={classes.themeSwitch}>
        <SidebarThemeSwitch />
      </div>
    </div>
  );
};

/**
 * The theme of the compact menu: one button showing the current mode's icon,
 * each press the next one, in the switch's order — Clair, Sombre, Auto (the
 * system's setting), Clair… Its name and tooltip say the current mode and
 * the next.
 */
function ThemeCycleButton(props: {
  mode: ThemeMode;
  select: (next: ThemeMode) => void;
}) {
  const { mode, select } = props;
  const next =
    THEME_MODES[(THEME_MODES.indexOf(mode) + 1) % THEME_MODES.length];
  const label = `Thème : ${themeModeLabels[mode]}, passer à ${themeModeLabels[next]}`;
  return (
    <RailButton
      aria-label={label}
      icon={themeModeIcons[mode]}
      onPress={() => select(next)}
    />
  );
}
